<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, PropType, reactive, ref, watch } from 'vue';
// @ts-expect-error (components lack types)
import { AisClearRefinements, AisStats } from 'vue-instantsearch/vue3/es';
import { useRoute } from 'vue-router';

import ClearRefinements from '@/components/search/widgets/ClearRefinements.vue';
import CollapsibleRefinementList from '@/components/search/widgets/CollapsibleRefinementList.vue';
import CollapsibleSortBy from '@/components/search/widgets/CollapsibleSortBy.vue';
import ExpandedSortBy from '@/components/search/widgets/ExpandedSortBy.vue';
import SearchBox from '@/components/search/widgets/SearchBox.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import overlayScrollLock from '@/utils/overlayScrollLock';
import { scrollToTarget } from '@/utils/scrollTo';
import { RefinementMenu } from '@/utils/searchRefinementMenus';
import { waitABit } from '@/utils/waitABit';

const props = defineProps({
  refinementMenus: { required: true, type: Array as PropType<RefinementMenu[]> },
  sortByItems: { required: true, type: Array as PropType<{ label: string; value: string }[]> },
  // optional props
  menuLimit: { required: false, type: Number, default: 25 },
  panelGroupLocator: { required: false, type: String, default: 'panel-group' },
  whitelistedRefinements: {
    required: false,
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const route = useRoute();
const { flags } = useFeatureFlags();

const revealMobileMenu = ref(false);
const activePanel = ref<'filters' | 'sort'>();
const optionCountByFacet = reactive<Record<string, number>>({});
const showFilters = reactive<Record<'sortBy' | string, boolean>>({
  sortBy: false,
});
props.refinementMenus.forEach((menu) => {
  optionCountByFacet[menu.attribute] = 0;
  showFilters[menu.attribute] = false;
});

const panelHeader = computed(() => {
  if (activePanel.value === 'filters') return 'Filter';
  if (activePanel.value === 'sort') return 'Sort By';
  return 'Filter & Sort';
});

let killInitMenus = false;
const initMenus = () => {
  if (killInitMenus) return undefined;
  props.refinementMenus.forEach((menu) => {
    showFilters[menu.attribute] = false;
  });
  if (window.innerWidth >= 891 || flags.showMoreFiltersPLP || flags.showMoreFiltersSearchPage) {
    if (Object.values(optionCountByFacet).every((count) => count === 0)) {
      return waitABit({ timeout: 250, window }).then(() => {
        initMenus();
      });
    }
    const expansionLimit = 3;
    let expanded = 0;
    props.refinementMenus.forEach((menuWidget) => {
      if (expanded === expansionLimit) return;
      if (!optionCountByFacet[menuWidget.attribute]) return;
      showFilters[menuWidget.attribute] = true;
      expanded += 1;
    });
  }
  return undefined;
};

const closeOverlay = () => {
  overlayScrollLock.unlockOnClose();
  revealMobileMenu.value = false;
  activePanel.value = undefined;
};
const openOverlay = (panel?: 'filters' | 'sort') => {
  activePanel.value = panel;

  revealMobileMenu.value = true;
  overlayScrollLock.lockOnOpen();
};
defineExpose({ openOverlay });
const scrollToTopOfResults = () => {
  scrollToTarget('.ais-Hits');
};

let stopWatchingMenu: () => void;
onMounted(() => {
  overlayScrollLock.bindScrollListener();
  initMenus();

  stopWatchingMenu = watch(
    () => route.path,
    () => {
      props.refinementMenus.forEach((menu) => {
        optionCountByFacet[menu.attribute] = 0;
      });
      initMenus();
    },
  );
});
onBeforeUnmount(() => {
  overlayScrollLock.unbindScrollListener();
  killInitMenus = true;
  stopWatchingMenu();
});
</script>

<template>
  <div
    class="search-control-panel"
    :class="{ shown: revealMobileMenu }"
    data-test="search-control-panel"
  >
    <div class="panel-header md:hidden">
      <strong>{{ panelHeader }}</strong>
      <a href="#" class="close-overlay" @click.prevent="closeOverlay()" data-test="close-overlay"
        >✕</a
      >
    </div>
    <div class="hidden md:visible md:block">
      <slot name="search-bar">
        <SearchBox data-test="searchBox" />
      </slot>
      <div class="panel-heading with-border">
        <strong>FILTER BY</strong>
        <AisClearRefinements class="d-inline-block" :excluded-attributes="whitelistedRefinements">
          <template v-slot:default="{ canRefine, refine }">
            <div>
              <a
                v-show="canRefine"
                href="#"
                class="clear-filters"
                data-test="clear-filters"
                @click.prevent="refine"
                >Clear Filters</a
              >
            </div>
          </template>
        </AisClearRefinements>
      </div>
    </div>
    <div class="panel-group" :data-test="panelGroupLocator">
      <ExpandedSortBy
        v-show="activePanel === 'sort'"
        class="md:hidden"
        data-test="expanded-sort-by"
        :items="sortByItems"
      />
      <CollapsibleSortBy
        v-show="!activePanel"
        v-model="showFilters.sortBy"
        header="SORT BY"
        :items="sortByItems"
        class="md:hidden"
        data-test="mobile-sort-by"
      />
      <div v-show="activePanel !== 'sort'">
        <CollapsibleRefinementList
          v-for="menu in refinementMenus"
          v-model="showFilters[menu.attribute]"
          v-model:optionCount="optionCountByFacet[menu.attribute]"
          :key="menu.attribute"
          :header="menu.header"
          :attribute="menu.attribute"
          :operator="menu.operator"
          :limit="menuLimit"
        />
      </div>
    </div>
    <div class="flex justify-center panel-footer md:hidden">
      <ClearRefinements
        v-if="activePanel !== 'sort'"
        class="d-inline-block"
        :excluded-attributes="whitelistedRefinements"
        @click="closeOverlay"
      />
      <AisStats>
        <template v-slot:default="{ nbHits }">
          <button
            class="view-results"
            data-test="view-results"
            @click.prevent="
              closeOverlay();
              scrollToTopOfResults();
            "
          >
            See {{ nbHits }} Results
          </button>
        </template>
      </AisStats>
    </div>
  </div>
</template>

<style scoped>
.panel-group {
  @apply pb-16 md:pb-0;
}
</style>
